import { get, cloneDeep } from 'lodash';
import constants from 'src/constants';
import {
  inchesToFeet,
  inchesToMeters,
  inchesToFeetInches,
} from 'src/lib/conversionUtils';

const { SEGMENTS, SEGMENTS_MAPPING } = constants.measure;

/**
 * Class representing the segments of a building.
 * Segments are defined as the different edges of a building.
 * The class calculates and organizes segments into different groups.
 * The groups are brick, roof and siding.
 * The constructor depends on data fetched from the
 * pro_premium_measurements.json.url property of the building model.
 */
export default class Segments {
  /**
   * All param lengths and areas are in inches.
   * @param {Object.<string, Object>} points
   * @param {Object.<string, Object>} edges
   * @param {Object.<string, Object>} facades
   * @param {Object.<string, Object>} groups
   */
  constructor({ points = {}, edges = {}, facades = {}, groups = {} }) {
    this.points = points;
    this.edges = edges;
    this.facades = facades;
    this.ignoredGroups = [
      'brick',
      'stucco',
      'concrete',
      'wrap',
      'stone',
      'unknown',
    ];
    this.groups = this.filterGroups(groups);
  }

  /**
   * Remove groups which can be ignored.
   * @param {Object.<string, Object>} groups
   * @returns {Object.<string, Object>}
   * @private
   */
  filterGroups(groups) {
    return Object.keys(groups).reduce((filteredGroups, groupName) => {
      if (!this.ignoredGroups.includes(groupName)) {
        filteredGroups[groupName] = groups[groupName];
      }
      return filteredGroups;
    }, {});
  }

  /**
   * Add additional length properties to an item.
   * The item must already have a length.inches property.
   * Additional length properties are meters, feet and feetInches (e.g. 4'2").
   * @param {Object.<string, Object>} item must have a length.inches property
   * @returns {Object.<string, Object>}
   * @private
   */
  addAllLengthPropertiesToItem(item) {
    const { inches } = item.length;
    if (inches === undefined) return item;
    const augmentedItem = cloneDeep(item);
    augmentedItem.length = {
      ...item.length,
      feet: inchesToFeet(inches),
      meters: inchesToMeters(inches),
      feetInches: inchesToFeetInches(inches).feetinches,
    };
    return augmentedItem;
  }

  /**
   * Initialize the item object for a segment type.
   * @param {string} type e.g. eaves, inside_corner, rakes, etc.
   * @returns {Object}
   * @private
   */
  initItem(segmentType) {
    return {
      key: segmentType,
      displayName: SEGMENTS_MAPPING[segmentType] || segmentType,
      count: 0,
      edges: [],
      length: {
        inches: 0,
        feet: 0,
        meters: 0,
        feetInches: '',
      },
    };
  }

  /**
   * Get edge data (including starting and ending points) for edge id
   * @param {string} edgeId
   * @returns {Object}
   * @private
   */
  getEdgeData(edgeId, facadeId) {
    let result = { ...this.edges[edgeId] };
    const startingPointId = get(this.edges, [edgeId, 'points', 0]);
    const endingPointId = get(this.edges, [edgeId, 'points', 1]);
    if (this.points[startingPointId] && this.points[endingPointId]) {
      result = {
        ...result,
        start: cloneDeep(this.points[startingPointId]),
        end: cloneDeep(this.points[endingPointId]),
        id: edgeId,
        facades: [facadeId],
      };
    }
    return result;
  }

  /**
   * Iterate through the edges (aka segments) of the facades of a group.
   * Build and return a hash of the segment types (keys) and their accumulated details (values).
   * The accumulated details include the total length and count for each type of segment.
   * @param {string} groupKey brick, roof or siding
   * @returns {Object}
   * @private
   */
  buildSegmentHash(groupKey) {
    // multiple facades can have the same edge so ensure
    // the edges are not double counted
    const edgeIdsAlreadyCounted = {};

    return this.groups[groupKey].facade_ids.reduce((acc, facadeId) => {
      const facade = this.facades[facadeId];
      facade.edges.forEach((edge) => {
        const { type, id } = edge;
        if (!acc[type]) acc[type] = this.initItem(type);
        if (!edgeIdsAlreadyCounted[id]) {
          // update accumulated details for segment type
          acc[type].count += 1;
          acc[type].length.inches += this.edges[id].length;
          acc[type].edges.push(this.getEdgeData(id, facadeId));
          edgeIdsAlreadyCounted[id] = true;
        } else {
          // edges can have multiple facades, so need to add multiple facades here
          const edgeIndex = acc[type].edges.findIndex((edge) => edge.id === id);
          if (get(acc, `${type}.edges.${edgeIndex}.facades`)) {
            // fix for [NS-277]
            acc[type].edges[edgeIndex].facades.push(facadeId);
          }
        }
      });
      return acc;
    }, {});
  }

  /**
   * Get the calculated items for a given group key
   * @param {string} groupKey brick, roof or siding
   * @returns {Array.<Object>}
   * @private
   */
  getItems(groupKey) {
    return Object.values(this.buildSegmentHash(groupKey)).map(
      this.addAllLengthPropertiesToItem,
    );
  }

  /**
   * @returns {Object}
   * @private
   */
  initTotals() {
    return {
      count: 0,
      length: {
        inches: 0,
        feet: 0,
        meters: 0,
        feetInches: '',
      },
    };
  }

  /**
   * Calculate the total count and length properties for a group of items
   * @param {Object.<string, Object>} items
   * @returns {Object.<string, number>}
   * @private
   */
  getTotals(items) {
    let totals = Object.values(items).reduce((totals, item) => {
      totals.count += item.count;
      totals.length.inches += item.length.inches;
      return totals;
    }, this.initTotals());

    totals = this.addAllLengthPropertiesToItem(totals);
    return totals;
  }

  /**
   * This is the only method in the class which is public.
   * It builds and formats the segments data.
   * @returns {Object.<string, Object>}
   * @public
   */
  getData() {
    return Object.keys(this.groups).reduce((acc, groupKey) => {
      const items = this.getItems(groupKey);
      if (SEGMENTS[groupKey] !== undefined) {
        acc[SEGMENTS[groupKey]] = {
          items,
          total: this.getTotals(items),
        };
      }
      return acc;
    }, {});
  }
}
